import React from 'react';
import Header from './HeaderHome';
import Footer from './FooterHome';

const EducationForPoor = () => {
  // ── Key Programmes data ──
  const programmes = [
    {
      icon: "🎓",
      title: "Scholarships & Fee Support",
      desc: "Providing full and partial scholarships to cover school fees, college tuition, and other educational expenses for deserving students from low-income families.",
    },
    {
      icon: "📚",
      title: "Books & Study Materials",
      desc: "Distributing textbooks, notebooks, stationery, reference materials, and digital learning resources to ensure every child has the tools they need to succeed.",
    },
    {
      icon: "👕",
      title: "School Uniforms & Supplies",
      desc: "Providing school uniforms, bags, shoes, and other essential supplies to help children attend school with confidence and dignity.",
    },
    {
      icon: "💻",
      title: "Digital Education & Technology",
      desc: "Bridging the digital divide by providing computers, tablets, internet access, and digital literacy training to students in underserved communities.",
    },
    {
      icon: "👩‍🏫",
      title: "Tutoring & Mentorship",
      desc: "Offering after-school tutoring, career guidance, mentorship programmes, and personality development workshops to help students excel academically and personally.",
    },
    {
      icon: "🏫",
      title: "Infrastructure Support",
      desc: "Building and renovating classrooms, libraries, computer labs, and sanitary facilities in government schools and rural areas to create conducive learning environments.",
    },
  ];

  // ── Impact Stats ──
  const stats = [
    { number: "4,500+", label: "Students Supported" },
    { number: "2,800+", label: "Scholarships Awarded" },
    { number: "12,000+", label: "Books Distributed" },
    { number: "150+", label: "Schools Supported" },
  ];

  // ── How You Can Help ──
  const helpOptions = [
    { icon: "🎓", title: "Sponsor a Child", desc: "Fund a child's complete education including fees, books, and supplies." },
    { icon: "📚", title: "Donate Books", desc: "Contribute new or gently used textbooks, storybooks, and study materials." },
    { icon: "💻", title: "Support Digital Learning", desc: "Provide computers, tablets, or fund internet access for students." },
  ];

  return (
    <>
      <Header />

      {/* ========== HERO ========== */}
      <section className="education-hero relative text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-block bg-primary/80 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium mb-4">
              <i className="fas fa-graduation-cap mr-2"></i> Our Service
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
              Education for Poor Students
            </h1>
            <p className="text-xl md:text-2xl text-yellow-100/90 font-light">
              Opening doors to knowledge, opportunity, and a brighter future for every child
            </p>
            <div className="w-24 h-1 bg-yellow-300 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* ========== OVERVIEW ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">
                Empowering Through Education
              </h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
            </div>
            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Education is the most powerful tool to break the cycle of poverty and transform lives. Yet, millions
              of children in India are denied this fundamental right due to financial constraints, lack of
              resources, and systemic barriers. At Venkatadri Seva Trust, our <strong>Education for Poor
                Students</strong> programme is dedicated to ensuring that every child, regardless of their economic
              background, has access to quality education.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              We provide comprehensive educational support—from scholarships and school supplies to digital
              learning and mentorship—to empower students to achieve their full potential. We believe that an
              educated child is not only a promise for their own future but also a catalyst for positive change
              in their family and community.
            </p>
          </div>
        </div>
      </section>

      {/* ========== VISION & MISSION ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="vision-card rounded-2xl p-8 text-white text-center">
              <div className="text-5xl mb-4">👁️</div>
              <h3 className="text-2xl font-bold mb-3">Our Vision</h3>
              <p className="text-yellow-50/90 text-lg leading-relaxed">
                A literate and empowered India where every child, regardless of economic status, has equal access
                to quality education and the opportunity to build a life of dignity and purpose.
              </p>
            </div>
            <div className="vision-card rounded-2xl p-8 text-white text-center">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold mb-3">Our Mission</h3>
              <p className="text-yellow-50/90 text-lg leading-relaxed">
                To remove financial and resource barriers to education by providing scholarships, learning
                materials, technology access, and mentorship—enabling every child to learn, grow, and succeed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== KEY PROGRAMMES ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Key Programmes</h2>
            <p className="text-yellow-100 mt-2 text-lg">
              Comprehensive educational support from school to career readiness
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {programmes.map((programme, idx) => (
              <div
                key={idx}
                className="program-card bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center border-t-4 border-primary"
              >
                <div className="service-icon"><span>{programme.icon}</span></div>
                <h3 className="text-xl font-semibold text-gray-800">{programme.title}</h3>
                <p className="text-gray-600 mt-2 text-sm">{programme.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== IMPACT / STATS ========== */}
      {/* <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Our Impact</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mt-3 rounded-full"></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto text-center text-white">
            {stats.map((stat, idx) => (
              <div key={idx}>
                <div className="text-4xl md:text-5xl font-bold text-yellow-300">{stat.number}</div>
                <p className="text-yellow-100/80 text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* ========== HOW YOU CAN HELP ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">How You Can Help</h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
              <p className="text-gray-600 mt-4 text-lg">
                Your support can unlock a child's potential and change their life forever.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {helpOptions.map((option, idx) => (
                <div key={idx} className="text-center p-4 rounded-lg bg-gray-50 hover:bg-yellow-50 transition-smooth">
                  <div className="text-4xl text-primary mb-3">{option.icon}</div>
                  <h4 className="font-bold text-gray-800">{option.title}</h4>
                  <p className="text-gray-600 text-sm mt-1">{option.desc}</p>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <a
                href="/donate"
                className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-primary-dark transition-smooth inline-block"
              >
                <i className="fas fa-hand-holding-heart mr-2"></i> Support Education
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIAL ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <div className="text-6xl mb-4">“</div>
            <p className="text-xl md:text-2xl font-light leading-relaxed italic">
              My father is a daily wage labourer and couldn't afford my college fees. Venkatadri Seva Trust gave
              me a full scholarship, books, and even a laptop. Today, I am a software engineer. They didn't just
              fund my education—they changed the course of my entire family.
            </p>
            <div className="mt-6">
              <p className="font-semibold text-yellow-200">— Kiran, 24</p>
              <p className="text-yellow-100/70 text-sm">Bengaluru</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== JOIN OUR MISSION ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4 text-center text-white">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Join Our Mission</h2>
            <div className="w-20 h-1 bg-yellow-300 mx-auto mb-6 rounded-full"></div>
            <p className="text-xl text-yellow-100/90 italic mb-6">
              "Education is the most powerful weapon which you can use to change the world."
            </p>
            <p className="text-lg text-white/80 leading-relaxed mb-8">
              Your generous support—through donations, volunteering, or partnerships—helps us reach more
              students, provide life-changing educational opportunities, and build a more equitable and
              prosperous society.
            </p>
            <p className="text-xl text-yellow-100 font-light mb-8">
              Together, we can ensure that no child is left behind in the pursuit of knowledge and a brighter
              future.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/donate"
                className="bg-white text-primary px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-smooth"
              >
                <i className="fas fa-heart mr-2"></i> Donate Now
              </a>
              <a
                href="/volunteer"
                className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/10 transition-smooth"
              >
                <i className="fas fa-hand-peace mr-2"></i> Volunteer
              </a>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default EducationForPoor;